import {
  registerNode,
  IG6GraphEvent,
  ModelConfig,
  IBBox,
  IShape,
  IShapeBase,
  Item,
} from '@antv/g6'
import numeral from 'numeral'
import type { GraphNodeCustomAttrs, UserJourneyNode } from './type'
import { GraphStates, fittingString } from './utils'
import type { IVueI18n } from 'vue-i18n'
import { basicAction } from '@pt/views/dashboard/material/detail/utils'

const CustomNodeName = 'custom-node'

const borderWidth = 2
const paddingLeft = 16
const paddingTop = 12

const keyShapeName = 'key-shape'
const RightArrowCircleShapeName = 'arrow-circle-shape'
const RightArrowPathShapeName = 'arrow-path-shape'
const LeftCircleShapeName = 'left-circle-shape'
const EditButton = 'edit-button'

const RightArrow = [RightArrowCircleShapeName, RightArrowPathShapeName]

const startSignName = 'start-sign'
const nodeTitleName = 'node-title'

// https://g6-v4.antv.vision/manual/middle/elements/nodes/custom-node
export function registerCustomNode(options: {
  t: IVueI18n['t']
  nameEditable: boolean
}): void {
  const { t, nameEditable } = options

  registerNode(
    'custom-node',
    {
      draw: (cfg: ModelConfig & GraphNodeCustomAttrs, group) => {
        const draggable = true
        const { size, nodeType = 'rect', readonly } = cfg
        const nodeData = cfg.data as UserJourneyNode
        const selectedStyle = cfg.style?.[GraphStates.Selected] as
          | ModelConfig['style']
          | undefined

        const [_width = 0, _height = 0] =
          (typeof size === 'number' ? [size, size] : size) ?? []
        const width = Math.max(0, _width - borderWidth)
        const height = Math.max(0, _height - borderWidth)
        const keyShape = group.addShape('rect', {
          zIndex: 0,

          attrs: {
            fill: '#fff',
            stroke: 'rgb(102, 102, 102)',
            strokeOpacity: 0.08,
            lineWidth: borderWidth,
            radius: 4,
            width,
            height,
            // cursor: 'pointer',
          },
          // must be assigned in G6 3.3 and later versions. it can be any string you want, but should be unique in a custom item type
          name: keyShapeName,
        })

        // 起点标识
        if (!readonly) {
          const startSignPositionOffset = 0.2
          const startSignWidth = 4

          group.addShape('rect', {
            visible: false,
            draggable,
            zIndex: 0,

            attrs: {
              x: borderWidth / 2 - startSignPositionOffset,
              y: borderWidth / 2 - startSignPositionOffset,

              fill: selectedStyle?.stroke ?? '#F3AD02',
              fillOpacity: selectedStyle?.strokeOpacity ?? 1,
              strokeOpacity: 0,
              lineWidth: 0,
              radius: [startSignWidth / 2, 0, 0, startSignWidth / 2],
              width: startSignWidth,
              height: height - (borderWidth - startSignPositionOffset * 2),
            },
            name: startSignName,
          })
        }

        const isBasicAction = () => {
          const actionList = basicAction.map((item) => item.id)
          const index = nodeData.order ?? cfg.id
          return actionList.includes(index)
        }

        // 不包含gs的基础埋点
        const isNoDataBasicAction = () => {
          const actionList = basicAction
            .filter((item) => item.id !== 'gs')
            .map((item) => item.id)
          const index = nodeData.order ?? cfg.id
          return actionList.includes(index)
        }

        // 索引
        const indexBoxSize = 24
        const fontSize = 14
        if (!isBasicAction()) {
          group.addShape('rect', {
            draggable,
            zIndex: 1,

            attrs: {
              x: paddingLeft,
              y: paddingTop,
              fill: '#999',
              fillOpacity: 0.08,
              stroke: 'rgb(102, 102, 102)',
              strokeOpacity: 0.08,
              lineWidth: 1,
              radius: 2,
              width: indexBoxSize,
              height: indexBoxSize,
            },
            // must be assigned in G6 3.3 and later versions. it can be any string you want, but should be unique in a custom item type
            name: 'index-box',
          })
          group.addShape('text', {
            draggable,
            attrs: {
              x: paddingLeft + indexBoxSize / 2,
              y: paddingTop + indexBoxSize / 2 + fontSize / 2,

              // text: nodeData.order ?? cfg.id,
              text: nodeData.order ?? cfg.id,
              textAlign: 'center',
              fill: '#666',
              fontSize: fontSize,
            },
          })
        }
        // 标题
        const getBasicActionName = (key: string) => {
          const map = {
            impression: t('userJourney.basicAction.imp').toString(),
            ws: t('userJourney.basicAction.ws').toString(),
            gs: t('userJourney.basicAction.gs').toString(),
          }
          return map[key] || key
        }
        const textX = paddingLeft + (isBasicAction() ? 0 : indexBoxSize + 8)
        const textWidth = width - textX - paddingLeft
        const titleText = String(nodeData.label ?? cfg.id) as any
        group.addShape('text', {
          draggable,
          attrs: {
            x: textX,
            y: paddingTop + indexBoxSize / 2 + fontSize / 2,

            sourceText: titleText,
            text: fittingString(
              getBasicActionName(titleText),
              textWidth,
              fontSize,
            ),
            textAlign: 'left',
            fill: '#333',
            fontSize: fontSize,
          },

          name: nodeTitleName,
        })

        if (!readonly) {
          const bbox = group.getBBox() as IBBox

          const x = bbox.x + Number(bbox.width) - borderWidth / 2
          const y = bbox.y + bbox.height * 0.5

          // Left
          group?.addShape('circle', {
            visible: false,
            draggable,
            attrs: {
              r: 5,
              fill: '#fff',
              // F5BD35 按下的颜色
              stroke: '#F3AD02',
              lineWidth: 2,
              cursor: 'pointer',

              x: bbox.x + 1,
              y,
            },

            name: LeftCircleShapeName,
          })

          // Right
          group?.addShape('circle', {
            visible: false,
            draggable,
            attrs: {
              r: 10,
              // F5BD35 按下的颜色
              fill: '#F3AD02',
              cursor: 'pointer',

              x,
              y,
            },

            name: RightArrowCircleShapeName,
          })

          // <path d="M9.08578 5.50002L1 5.50002V6.50002L9.08578 6.50002L5.69669 9.88908L6.40379 10.5962L10.6464 6.35358C10.8417 6.15832 10.8417 5.84173 10.6464 5.64647L6.40379 1.40381L5.69669 2.11091L9.08578 5.50002Z" fill="#666666"/>
          const pathShape = group?.addShape('path', {
            visible: false,
            draggable: true,
            attrs: {
              path: `M9.08578 5.50002L1 5.50002V6.50002L9.08578 6.50002L5.69669 9.88908L6.40379 10.5962L10.6464 6.35358C10.8417 6.15832 10.8417 5.84173 10.6464 5.64647L6.40379 1.40381L5.69669 2.11091L9.08578 5.50002Z`,
              lineWidth: 0,
              fill: '#fff',
              cursor: 'pointer',
            },

            name: RightArrowPathShapeName,
          })

          if (pathShape) {
            const pathShapeBox = pathShape.getBBox() as IBBox
            pathShape.translate(
              x - pathShapeBox.x - pathShapeBox.width / 2,
              y - pathShapeBox.y - pathShapeBox.height / 2,
            )
          }

          // 只针对pt对外平台支持编辑action名称功能
          if (nameEditable) {
            // <path d="M9.08578 5.50002L1 5.50002V6.50002L9.08578 6.50002L5.69669 9.88908L6.40379 10.5962L10.6464 6.35358C10.8417 6.15832 10.8417 5.84173 10.6464 5.64647L6.40379 1.40381L5.69669 2.11091L9.08578 5.50002Z" fill="#666666"/>
            const editShape = group?.addShape('path', {
              visible: true,
              draggable: true,
              attrs: {
                path: `M6.41421 15.9855L16.5563 5.84332L15.1421 4.42911L5 14.5713V15.9855H6.41421ZM7.24264 17.9855H3.5C3.22386 17.9855 3 17.7616 3 17.4855V13.7429L14.435 2.30779C14.8256 1.91726 15.4587 1.91726 15.8492 2.30779L18.6777 5.13621C19.0682 5.52674 19.0682 6.1599 18.6777 6.55043L7.24264 17.9855ZM3 19.9855H21V21.9855H3V19.9855Z`,
                lineWidth: 0,
                fill: '#666',
                cursor: 'pointer',
                nodeData,
              },
              name: EditButton,
            })

            if (editShape) {
              const pathShapeBox = editShape.getBBox() as IBBox
              editShape.translate(
                x - pathShapeBox.x - pathShapeBox.width - 40,
                y - pathShapeBox.y - pathShapeBox.height / 2,
                // x - 100,
                // y - pathShapeBox.y - pathShapeBox.height / 2,
              )
            }
          }
        }

        if (nodeType === 'card') {
          // Divider
          group.addShape('rect', {
            draggable,
            zIndex: 1,

            attrs: {
              x: paddingLeft,
              y: paddingTop + indexBoxSize + 12,
              fill: '#000',
              fillOpacity: 0.08,

              lineWidth: 0,
              width: width - paddingLeft * 2,
              height: 1,
            },
            // must be assigned in G6 3.3 and later versions. it can be any string you want, but should be unique in a custom item type
            name: 'divider',
          })
          // 到达数
          // 流转到下一节点数
          // 流失数
          // 跳转数
          // 121(auto) 4 64 4 64

          const {
            reach = 0,

            flows = 0,
            flowsRate = 0,

            install = 0,
            installRate = 0,

            loss = 0,
            lossRate = 0,
          } = cfg.data ?? {}
          const _percent = (value: number): string =>
            `${Number((value * 100).toFixed(2))}%`
          const rows: Array<{
            text: string
            fill: string
            value: string
            percent?: string
          }> = [
            {
              fill: '#333',
              text: t('userJourney.fields.reach').toString(), // 到达数
              value: numeral(reach).format('0,0'),
            },
            ...(!isNoDataBasicAction()
              ? [
                  {
                    fill: '#333',
                    text: t('userJourney.fields.flowsNext').toString(), // 流转到下一节点数
                    value: numeral(flows).format('0,0'),
                    percent: _percent(flowsRate),
                  },
                  {
                    fill: '#EA1B26',
                    text: t('userJourney.fields.loss').toString(), // 流失数
                    value: numeral(loss).format('0,0'),
                    percent: _percent(lossRate),
                  },
                  {
                    fill: '#52C41A',
                    text: t('userJourney.fields.install').toString(), // 跳转数
                    value: numeral(install).format('0,0'),
                    percent: _percent(installRate),
                  },
                ]
              : []),
          ]
          const left = paddingLeft
          const top = paddingTop + indexBoxSize + 12 + 1 + 12
          rows.forEach((row, index) => {
            const y = top + index * (22 + 4)
            group.addShape('text', {
              draggable,
              attrs: {
                x: left,
                y,
                textBaseline: 'top',

                fill: row.fill,
                text: row.text,
                textAlign: 'left',
                fontSize: fontSize,
              },
            })

            group.addShape('text', {
              draggable,
              attrs: {
                x: width - paddingLeft - 64,
                y,
                textBaseline: 'top',

                fill: row.fill,
                text: row.value,
                textAlign: 'right',
                fontSize: fontSize,
              },
            })

            group.addShape('text', {
              draggable,
              attrs: {
                x: width - paddingLeft,
                y,
                textBaseline: 'top',

                fill: row.fill,
                text: row.percent ?? '',
                textAlign: 'right',
                fontSize: fontSize,
              },
            })
          })
        }

        return keyShape
      },
      setState(name, value, item) {
        const shape = item?.get<IShape>('keyShape')

        const selected = Boolean(item?.hasState(GraphStates.Selected))
        const actived = Boolean(item?.hasState(GraphStates.Actived))

        if (item?.hasState(GraphStates.Selected)) {
          shape?.attr(item?.getStateStyle(GraphStates.Selected))
        } else if (item?.hasState(GraphStates.Actived)) {
          shape?.attr(item?.getStateStyle(GraphStates.Actived))
        } else if (item?.hasState(GraphStates.Hover)) {
          shape?.attr(item?.getStateStyle(GraphStates.Hover))
        } else {
          const style = item?.getOriginStyle()
          style && style[keyShapeName] && shape?.attr(style[keyShapeName])
        }

        // 编辑 选中 时候，需要展示 箭头
        setSelectedItemVisible(selected, item)

        // 编辑 激活 时候，需要展示 接收锚点
        setActivedItemVisible(!selected && actived, item)
      },
    },
    'single-node',
  )
}

export function shouldBeginDrag(target: IG6GraphEvent['target']): boolean {
  return !RightArrow.includes(target.get('name'))
}

function setSelectedItemVisible(visible: boolean, item?: Item): void {
  RightArrow.forEach((name) => {
    item
      ?.getContainer()
      .findAll((ele) => ele.get('name') === name)
      .forEach((ele) => {
        if (visible) {
          ele.show()
        } else {
          ele.hide()
        }
      })
  })
}

function setActivedItemVisible(visible: boolean, item?: Item): void {
  const arr = [LeftCircleShapeName]
  arr.forEach((name) => {
    item
      ?.getContainer()
      .findAll((ele) => ele.get('name') === name)
      .forEach((ele) => {
        if (visible) {
          ele.show()
        } else {
          ele.hide()
        }
      })
  })
}

export function updateStartSignVisible(visible: boolean, item?: Item): void {
  item
    ?.getContainer()
    .findAll((ele) => ele.get('name') === startSignName)
    .forEach((ele) => {
      if (visible) {
        ele.show()
      } else {
        ele.hide()
      }
    })
}

export function isNodeTitle(item: IShapeBase): boolean {
  return item.get('name') === nodeTitleName
}
